import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { Pagination } from "swiper/modules";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import type { discoveryWidgetPostMagazineQueueQuery$key } from "scmp-app/queries/__generated__/discoveryWidgetPostMagazineQueueQuery.graphql";

import Logo from "./logo.svg";
import {
  Container,
  Footer,
  Header,
  ItemsContainer,
  MoreLink,
  Publication,
  StyledColumnContainer,
  StyledContentItemPostMagazineDiscovery,
  StyledContentItemPostMagazineDiscoverySponsor,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperContainer,
  Title,
} from "./styles";

type Props = {
  className?: string;
  reference: discoveryWidgetPostMagazineQueueQuery$key;
};

export const PostMagazineDiscoveryWidget: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment discoveryWidgetPostMagazineQueueQuery on Query {
        postMagazineLast7Days: queue(
          filter: { name: "scmp_trending_section_scmp_trending_section_postmag_last_7_days" }
        ) {
          items(first: 6) {
            edges {
              node {
                ... on Article {
                  entityId
                  ...postMagazineDiscoveryItemContent
                }
              }
            }
          }
        }
        postMagazineSponsor: queue(filter: { name: "discovery_module_discovery_module_71" }) {
          items(first: 1) {
            edges {
              node {
                ... on Article {
                  ...postMagazineDiscoveryItemContent
                }
              }
            }
          }
        }
        postMagazineSection: section(filter: { entityId: "71" }) {
          ...entityLink
        }
      }
    `,
    reference,
  );

  const currentPageType = useCurrentPageType();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"));

  const renderedItems = useMemo(() => {
    const [sponsorItem] = data?.postMagazineSponsor?.items?.edges ?? [];
    const last7DaysItems = data?.postMagazineLast7Days?.items?.edges ?? [];

    const sponsorNode = sponsorItem?.node ? (
      <StyledContentItemPostMagazineDiscoverySponsor key="sponsor" reference={sponsorItem?.node} />
    ) : null;
    const [firstNode, ...resetNodes] = last7DaysItems.map(({ node }, index) => (
      <StyledContentItemPostMagazineDiscovery
        key={node.entityId}
        reference={node}
        withSummary={index === 0}
      />
    ));

    const results = isMobile
      ? [firstNode, sponsorNode, ...resetNodes]
      : [firstNode, ...resetNodes, sponsorNode];

    return results.filter(notEmpty);
  }, [
    data?.postMagazineLast7Days?.items?.edges,
    data?.postMagazineSponsor?.items?.edges,
    isMobile,
  ]);

  const renderSwiper = useCallback(() => {
    const items = renderedItems ?? [];

    const groupedItems = [];
    for (let index = 0; index < items.length; index += 2) {
      groupedItems.push(items.slice(index, index + 2));
    }

    return (
      <SwiperContainer>
        <StyledSwiper modules={[Pagination]} pagination={true} slidesPerView={1} spaceBetween={16}>
          {groupedItems.map((group, groupIndex) => (
            <StyledSwiperSlide key={groupIndex}>
              <StyledColumnContainer>
                {group.map((item, index) => (
                  <div key={index} style={{ width: "100%" }}>
                    {item}
                  </div>
                ))}
              </StyledColumnContainer>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </SwiperContainer>
    );
  }, [renderedItems]);

  if (renderedItems?.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.PostMagazine,
        pgtype: currentPageType,
      }}
    >
      <Container className={className}>
        <Header>
          <Title>HIGHLIGHTS FROM</Title>
          <Logo />
        </Header>
        <ItemsContainer>{isMobile ? renderSwiper() : renderedItems}</ItemsContainer>
        <Footer>
          <MoreLink reference={data.postMagazineSection}>MORE IN POSTMAG</MoreLink>
          <Publication>{`Content from SCMP's Specialist Publications`}</Publication>
        </Footer>
      </Container>
    </BaseLinkContextProvider>
  );
};

PostMagazineDiscoveryWidget.displayName = "PostMagazineDiscoveryWidget";
